<template>
  <div class="athlete-card"  @click.prevent="$emit('viewAthlete', athlete.id)">
    <div class="athlete-header">
      <h4 class="athlete-name">{{ athlete.name }} {{ athlete.surname }}</h4>
    </div>

    <div class="athlete-body">
      <img :src="athlete.img_path || defaultAvatar" alt="Athlete Image" class="athlete-image" />

      <div class="athlete-info">
        <div class="country-badge" v-if="statsCountryCode">
          <span class="country-flag" aria-hidden="true" v-if="countryFlag">{{ countryFlag }}</span>
          <span class="country-code">{{ statsCountryCode }}</span>
        </div>

        <div class="stat-grid">
          <div class="stat-card">
            <span class="stat-label">{{ $t('athletes.stats.competitions') }}</span>
            <span class="stat-value">{{ stats.competitions_count }}</span>
          </div>

          <div class="stat-card">
            <span class="stat-label">{{ $t('athletes.stats.average_round_score') }}</span>
            <span class="stat-value">{{ averageRoundScoreDisplay }}</span>
          </div>

          <div class="stat-card podium-card">
            <span class="stat-label">{{ $t('athletes.stats.podiums') }}</span>
            <div class="podium-grid">
              <div class="podium-item">
                <span class="podium-rank">{{ $t('athletes.stats.first') }}</span>
                <span class="podium-value">{{ topPositions.first }}</span>
              </div>
              <div class="podium-item">
                <span class="podium-rank">{{ $t('athletes.stats.second') }}</span>
                <span class="podium-value">{{ topPositions.second }}</span>
              </div>
              <div class="podium-item">
                <span class="podium-rank">{{ $t('athletes.stats.third') }}</span>
                <span class="podium-value">{{ topPositions.third }}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>


<script>
export default {
  name: "AthleteCard",
  props: {
    athlete: {
      type: Object,
      required: true,
    }
  },
  data() {
    return {
      defaultAvatar: require("@/assets/user.png"),
    };
  },
  computed:{
    stats() {
      const normalize = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
      };
      const base = this.athlete?.stats || {};
      const topPositions = base.top_positions || {};
      const normalizedAverageRoundScore = Number(base.average_round_score_normalized);
      const fallbackAverageRoundScore = Number(base.average_round_score);

      return {
        country_code: (base.country_code || '').toUpperCase(),
        competitions_count: normalize(base.competitions_count),
        average_round_score: Number.isFinite(normalizedAverageRoundScore)
          ? normalizedAverageRoundScore
          : (Number.isFinite(fallbackAverageRoundScore) ? fallbackAverageRoundScore : null),
        average_round_score_raw: Number.isFinite(Number(base.average_round_score_raw)) ? Number(base.average_round_score_raw) : null,
        top_positions: {
          first: normalize(topPositions.first),
          second: normalize(topPositions.second),
          third: normalize(topPositions.third),
        },
      };
    },
    topPositions() {
      return this.stats.top_positions;
    },
    statsCountryCode() {
      return this.stats.country_code;
    },
    averageRoundScoreDisplay() {
      if (this.stats.average_round_score === null) {
        return '-';
      }
      return this.stats.average_round_score.toFixed(2);
    },
    countryFlag() {
      return this.statsCountryCode ? this.codeToFlag(this.statsCountryCode) : '';
    }
  },
  methods: {
    codeToFlag(code = '') {
      if (!code || code.length < 2) {
        return '';
      }
      const letters = code
          .toUpperCase()
          .replace(/[^A-Z]/g, '')
          .slice(0, 2);
      if (letters.length < 2) {
        return '';
      }
      return letters
          .split('')
          .map(char => String.fromCodePoint(127397 + char.charCodeAt(0)))
          .join('');
    }
  }
};
</script>

<style scoped>
.athlete-card {
  background-color: white;
  border-radius: 10px;
  padding: 0;
  overflow: hidden;
  margin-bottom: 16px;
  box-shadow: 0 0 10px rgba(0, 0, 0, 0.05);
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  transition: box-shadow 0.3s ease;
  cursor: pointer;
}

.athlete-card:hover {
  box-shadow: 0 0 12px rgba(0, 0, 0, 0.1);
}

/* 🔹 Header */
.athlete-header {
  background-color: #f7f7f7;
  padding: 10px 12px;
  border-bottom: 1px solid #ddd;
  display: flex;
  align-items: center;
}

.athlete-name {
  font-size: 16px;
  font-weight: 600;
  margin: 0;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

/* 🔸 Cuerpo con imagen e info */
.athlete-body {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px;
}

.athlete-image {
  width: 70px;
  height: 70px;
  border-radius: 50%;
  object-fit: cover;
}

.athlete-info {
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 10px;
}

.country-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-weight: 600;
  padding: 6px 10px;
  border-radius: 999px;
  background-color: #f5f7fa;
  border: 1px solid #e0e6ed;
  width: fit-content;
}

.country-flag {
  font-size: 18px;
  line-height: 1;
  /* Emoji-capable stack so flags render across platforms (incl. Windows) */
  font-family: "Twemoji Mozilla", "Noto Color Emoji", "Segoe UI Emoji", "Apple Color Emoji", "Segoe UI", system-ui, sans-serif;
}

.country-code {
  font-size: 12px;
  letter-spacing: 0.08em;
}

.stat-grid {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
}

.stat-card {
  flex: 1;
  min-width: 120px;
  background-color: #fafafa;
  border: 1px solid #ececec;
  border-radius: 8px;
  padding: 10px;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.stat-label {
  font-size: 12px;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.06em;
  text-align: center;
}

.stat-value {
  font-size: 20px;
  font-weight: 600;
  color: #111827;
  text-align: center;
}

.podium-card {
  flex: 2;
}

.podium-grid {
  display: flex;
  justify-content: space-between;
  gap: 8px;
}

.podium-item {
  flex: 1;
  display: flex;
  flex-direction: column;
  align-items: center;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 6px;
  padding: 6px 4px;
}

.podium-rank {
  font-size: 11px;
  color: #6b7280;
}

.podium-value {
  font-size: 16px;
  font-weight: 600;
}

@media (max-width: 576px) {
  .stat-grid {
    flex-direction: column;
  }

  .podium-grid {
    flex-direction: row;
  }

  .athlete-image {
    width: 80px;
    height: 80px;
  }
}

</style>
